import { useState } from "react";
import { Loader2, AlertTriangle, Play, Square } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const STEPS = [["research", "Research"], ["outline", "Outline"], ["chapters", "Chapters"], ["extras", "Listing & toolkit"], ["artwork", "Artwork"], ["render", "Typesetting"], ["package", "Packaging"]];

export const BuildProgress = ({ g, onResumed }) => {
  const [busy, setBusy] = useState(false);
  if (!g.running && !g.error && g.status !== "building") return null;
  const idx = STEPS.findIndex(([k]) => k === g.step);
  const resume = async () => {
    setBusy(true);
    try { await api.post(`/relocation/guides/${g.id}/build/build`); toast.success("Build resumed — finished steps are kept"); onResumed(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-[#3B1B48] text-[#FAF7F2] p-8" data-testid="reloc-build-progress">
      <div className="flex flex-wrap items-center gap-4 mb-5">
        {g.running ? <Loader2 className="w-5 h-5 animate-spin text-[#C59B27]" /> : <AlertTriangle className="w-5 h-5 text-[#F0B5B2]" />}
        <p className="font-display text-2xl flex-1">{g.running ? `Building · ${g.progress || 0}%` : "Build stopped"}</p>
        {g.running && (
          <button data-testid="reloc-build-stop" onClick={async () => { if (!window.confirm("Stop this build? Finished chapters are kept and you can resume later.")) return; try { await api.post(`/relocation/guides/${g.id}/stop`); toast.success("Build stopped"); onResumed(); } catch (e) { toast.error(errMsg(e)); } }}
            className="inline-flex items-center gap-2 border border-[#F0B5B2] text-[#F0B5B2] px-4 py-2 text-sm hover:bg-[#F0B5B2] hover:text-[#1C181D]"><Square className="w-4 h-4" />Stop build</button>
        )}
        {!g.running && g.status === "failed" && (
          <button data-testid="reloc-build-resume" onClick={resume} disabled={busy} className="inline-flex items-center gap-2 border border-[#C59B27] text-[#C59B27] px-4 py-2 text-sm hover:bg-[#C59B27] hover:text-[#1C181D]"><Play className="w-4 h-4" />Resume build</button>
        )}
      </div>
      <div className="h-1.5 bg-[#FAF7F2]/15 mb-5"><div className="h-full bg-[#C59B27] transition-[width] duration-700" style={{ width: `${g.progress || 0}%` }} data-testid="reloc-build-bar" /></div>
      <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs mb-5">
        {STEPS.map(([k, l], i) => <span key={k} className={i < idx ? "text-[#C59B27]" : i === idx ? "text-white" : "text-[#EFE6F3]/40"}>{i < idx ? "✓ " : ""}{l}</span>)}
      </div>
      {g.error && <p data-testid="reloc-build-error" className="text-sm text-[#F0B5B2] mb-4">{g.error}</p>}
      <div className="space-y-1 text-xs text-[#EFE6F3]/75 max-h-40 overflow-auto" data-testid="reloc-build-log">
        {[...g.logs].reverse().map((l, i) => <p key={i}><span className="text-[#C59B27]">{new Date(l.ts).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}</span> {l.msg}</p>)}
      </div>
      <p className="text-xs text-[#EFE6F3]/60 mt-4">A full guide takes about 25–40 minutes. You can leave this page; it keeps building.</p>
    </div>
  );
};
